import React, { useCallback, useEffect, useState } from 'react';
import { Confirm, useDataProvider, useLocaleState, useNotify, useTranslate } from 'react-admin';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { createProvisioningToken, getProvisioningTokens, revokeProvisioningToken } from '../../../rest/restClient';
import { getDateTimeToFullLocalString } from '../../../utils/date';
import { isAdminPermissions, isSuperAdmin } from '../../../helper/resourceHelper';


const DURATION_CHOICES = [
    { seconds: 2592000, labelKey: 'resources.devices.provisioning_tokens.duration_30d' },
    { seconds: 7776000, labelKey: 'resources.devices.provisioning_tokens.duration_90d' },
    { seconds: 15552000, labelKey: 'resources.devices.provisioning_tokens.duration_180d' },
    { seconds: 31536000, labelKey: 'resources.devices.provisioning_tokens.duration_1y' },
];

const DEFAULT_DURATION_SECONDS = 31536000;
const LABEL_MAX_LENGTH = 120;


const getDerivedStatus = (token) => {
    if (token.status && token.status.toUpperCase() === 'REVOKED') return 'revoked';
    if (token.expires_at && new Date(token.expires_at).getTime() < Date.now()) return 'expired';
    return 'active';
};


const getApiErrorMessage = (error, fallback) => {
    return error?.body?.description || error?.body?.message || fallback;
};


const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand('copy');
    } finally {
        document.body.removeChild(textArea);
    }
};


const StatusChip = ({ token }) => {
    const translate = useTranslate();
    const derivedStatus = getDerivedStatus(token);
    const chipProps = {
        active: { color: 'success', variant: 'filled' },
        expired: { color: 'warning', variant: 'outlined' },
        revoked: { color: 'error', variant: 'outlined' },
    }[derivedStatus];
    return (
        <Chip
            size="small"
            label={translate(`resources.devices.provisioning_tokens.status_${derivedStatus}`)}
            {...chipProps}
        />
    );
};


const OrganizationSelect = ({ value, onChange }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        dataProvider.getMany('organizations', {
            sort: { field: 'name', order: 'ASC' },
            pagination: { page: 1, perPage: 1000 },
        })
            .then(({ data }) => {
                if (active) {
                    setOrganizations(data || []);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (active) setLoading(false);
            });
        return () => { active = false; };
    }, [dataProvider]);

    return (
        <Autocomplete
            size="small"
            options={organizations}
            loading={loading}
            getOptionLabel={(org) => org.name || org.id}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={organizations.find((org) => org.id === value) || null}
            onChange={(e, org) => onChange(org ? org.id : null)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={translate('resources.devices.provisioning_tokens.organization')}
                    helperText={translate('resources.devices.provisioning_tokens.organization_helper')}
                />
            )}
            sx={{ margin: '0px 0px 10px 0px' }}
        />
    );
};


const TokenCreateForm = ({ organizationId, onCreated }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [label, setLabel] = useState('');
    const [durationSeconds, setDurationSeconds] = useState(DEFAULT_DURATION_SECONDS);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = () => {
        if (label.length > LABEL_MAX_LENGTH) return;
        setSubmitting(true);
        createProvisioningToken({
            label: label.trim() || undefined,
            durationSeconds,
            organizationId: isSuperAdmin() ? organizationId : undefined,
        })
            .then((response) => {
                setSubmitting(false);
                setLabel('');
                setDurationSeconds(DEFAULT_DURATION_SECONDS);
                onCreated(response.json);
            })
            .catch((error) => {
                setSubmitting(false);
                notify(getApiErrorMessage(error, translate('resources.devices.provisioning_tokens.create_error')), { type: 'error' });
            });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, margin: '0px 0px 10px 0px' }}>
                {translate('resources.devices.provisioning_tokens.create_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '10px', alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                <TextField
                    size="small"
                    fullWidth
                    label={translate('resources.devices.provisioning_tokens.label')}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    inputProps={{ maxLength: LABEL_MAX_LENGTH }}
                    helperText={`${translate('resources.devices.provisioning_tokens.label_helper')} — ${label.length}/${LABEL_MAX_LENGTH}`}
                />
                <TextField
                    size="small"
                    select
                    sx={{ minWidth: '120px' }}
                    label={translate('resources.devices.provisioning_tokens.duration')}
                    value={durationSeconds}
                    onChange={(e) => setDurationSeconds(Number(e.target.value))}
                >
                    {DURATION_CHOICES.map((choice) => (
                        <MenuItem key={choice.seconds} value={choice.seconds}>
                            {translate(choice.labelKey)}
                        </MenuItem>
                    ))}
                </TextField>
                <Button
                    variant="contained"
                    disabled={submitting}
                    onClick={handleSubmit}
                    sx={{ whiteSpace: 'nowrap', minWidth: '110px' }}
                >
                    {translate('resources.devices.provisioning_tokens.create_button')}
                </Button>
            </Box>
        </Box>
    );
};


const TokenCreatedPanel = ({ token, onDismiss }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [locale] = useLocaleState();
    const [copied, setCopied] = useState(false);
    const [copiedFeedback, setCopiedFeedback] = useState(false);
    const [openCloseConfirm, setOpenCloseConfirm] = useState(false);

    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    const handleNativeShare = () => {
        navigator.share({
            text: translate('resources.devices.provisioning_tokens.share_message'),
            url: token.url,
        })
            .then(() => {
                setCopied(true);
            })
            .catch(() => {
                // share sheet dismissed or sharing failed; Copy stays available
            });
    };

    const handleCopy = () => {
        copyToClipboard(token.url)
            .then(() => {
                setCopied(true);
                setCopiedFeedback(true);
                notify(translate('resources.devices.provisioning_tokens.link_copied'), { type: 'info' });
                setTimeout(() => setCopiedFeedback(false), 2000);
            })
            .catch(() => {
                notify(translate('resources.devices.provisioning_tokens.copy_error'), { type: 'warning' });
            });
    };

    const handleDismiss = () => {
        if (!copied) {
            setOpenCloseConfirm(true);
            return;
        }
        onDismiss();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Alert severity="warning" sx={{ margin: '0px 0px 10px 0px' }}>
                {translate('resources.devices.provisioning_tokens.url_warning')}
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '10px', alignItems: { xs: 'stretch', sm: 'center' } }}>
                <TextField
                    size="small"
                    fullWidth
                    value={token.url}
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '13px' } }}
                    onFocus={(e) => e.target.select()}
                />
                <Button
                    variant={canNativeShare ? 'outlined' : 'contained'}
                    color={copiedFeedback ? 'success' : 'primary'}
                    onClick={handleCopy}
                    sx={{ whiteSpace: 'nowrap', minWidth: '90px' }}
                >
                    {translate(copiedFeedback ? 'resources.devices.provisioning_tokens.copied' : 'resources.devices.provisioning_tokens.copy')}
                </Button>
            </Box>
            {canNativeShare && (
                <Button
                    variant="contained"
                    fullWidth
                    startIcon={<ShareIcon />}
                    onClick={handleNativeShare}
                    sx={{ margin: '10px 0px 0px 0px' }}
                >
                    {translate('resources.devices.provisioning_tokens.share')}
                </Button>
            )}
            {token.expires_at && (
                <Typography sx={{ fontSize: '12px', margin: '10px 0px 0px 0px' }} color="text.secondary">
                    {`${translate('resources.devices.provisioning_tokens.expires_at')}: ${getDateTimeToFullLocalString({ timestamp: token.expires_at, locale })}`}
                </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0px 0px 0px' }}>
                <Button variant="text" onClick={handleDismiss}>
                    {translate('resources.devices.provisioning_tokens.done')}
                </Button>
            </Box>
            <Confirm
                isOpen={openCloseConfirm}
                title={translate('resources.devices.provisioning_tokens.close_without_copy_title')}
                content={translate('resources.devices.provisioning_tokens.close_without_copy')}
                onConfirm={() => {
                    setOpenCloseConfirm(false);
                    onDismiss();
                }}
                onClose={() => setOpenCloseConfirm(false)}
            />
        </Box>
    );
};


const TokenRow = ({ token, onRevoked }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const theme = useTheme();
    const [locale] = useLocaleState();
    const [openRevokeConfirm, setOpenRevokeConfirm] = useState(false);
    const [revoking, setRevoking] = useState(false);

    const handleRevoke = () => {
        setRevoking(true);
        revokeProvisioningToken({ tokenId: token.id })
            .then(() => {
                setRevoking(false);
                setOpenRevokeConfirm(false);
                notify(translate('resources.devices.provisioning_tokens.revoke_success'), { type: 'info' });
                onRevoked();
            })
            .catch((error) => {
                setRevoking(false);
                setOpenRevokeConfirm(false);
                notify(getApiErrorMessage(error, translate('resources.devices.provisioning_tokens.revoke_error')), { type: 'warning' });
            });
    };

    const detailLine = [
        token.created_at && `${translate('resources.devices.provisioning_tokens.created_at')}: ${getDateTimeToFullLocalString({ timestamp: token.created_at, locale })}`,
        token.expires_at && `${translate('resources.devices.provisioning_tokens.expires_at')}: ${getDateTimeToFullLocalString({ timestamp: token.expires_at, locale })}`,
    ].filter(Boolean);

    const usageLine = [
        `${translate('resources.devices.provisioning_tokens.use_count')}: ${token.use_count ?? 0}`,
        token.last_used_at && `${translate('resources.devices.provisioning_tokens.last_used_at')}: ${getDateTimeToFullLocalString({ timestamp: token.last_used_at, locale })}`,
    ].filter(Boolean);

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: '4px', sm: '10px' }, padding: '10px 0px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} color={token.label ? 'text.primary' : 'text.secondary'}>
                        {token.label || translate('resources.devices.provisioning_tokens.untitled')}
                    </Typography>
                    <StatusChip token={token} />
                </Box>
                {detailLine.length > 0 && (
                    <Typography sx={{ fontSize: '12px' }} color="text.secondary">
                        {detailLine.join(' — ')}
                    </Typography>
                )}
                {usageLine.length > 0 && (
                    <Typography sx={{ fontSize: '12px' }} color="text.secondary">
                        {usageLine.join(' — ')}
                    </Typography>
                )}
            </Box>
            {getDerivedStatus(token) === 'active' && (
                <Button
                    variant="text"
                    size="small"
                    disabled={revoking}
                    onClick={() => setOpenRevokeConfirm(true)}
                    sx={{ color: theme.palette.error.main, whiteSpace: 'nowrap', alignSelf: { xs: 'flex-end', sm: 'center' } }}
                >
                    {translate('resources.devices.provisioning_tokens.revoke')}
                </Button>
            )}
            <Confirm
                isOpen={openRevokeConfirm}
                title={translate('resources.devices.provisioning_tokens.revoke_confirm_title')}
                content={translate('resources.devices.provisioning_tokens.revoke_confirm_content')}
                onConfirm={handleRevoke}
                onClose={() => setOpenRevokeConfirm(false)}
            />
        </Box>
    );
};


const TokenList = ({ tokens, loading, loadError, onRevoked }) => {
    const translate = useTranslate();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0px' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Alert severity="error" sx={{ margin: '10px 0px' }}>
                {translate('resources.devices.provisioning_tokens.load_error')}
            </Alert>
        );
    }

    if (!tokens || tokens.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '15px 0px' }}>
                <Typography sx={{ fontSize: '14px' }} color="text.secondary">
                    {translate('resources.devices.provisioning_tokens.empty')}
                </Typography>
                <Typography sx={{ fontSize: '12px', textAlign: 'center' }} color="text.secondary">
                    {translate('resources.devices.provisioning_tokens.empty_hint')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {tokens.map((token, index) => (
                <React.Fragment key={token.id}>
                    {index > 0 && <Divider />}
                    <TokenRow token={token} onRevoked={onRevoked} />
                </React.Fragment>
            ))}
        </Box>
    );
};


const ProvisioningTokens = ({ organizationId, onClose }) => {
    const translate = useTranslate();
    const [selectedOrganizationId, setSelectedOrganizationId] = useState(organizationId || null);
    const [tokens, setTokens] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [createdToken, setCreatedToken] = useState(null);

    const superAdmin = isSuperAdmin();

    const fetchTokens = useCallback(() => {
        setLoadError(false);
        getProvisioningTokens({ organizationId: superAdmin ? selectedOrganizationId : undefined })
            .then((response) => {
                setTokens(response.json || []);
                setLoading(false);
            })
            .catch(() => {
                setLoadError(true);
                setLoading(false);
            });
    }, [superAdmin, selectedOrganizationId]);

    useEffect(() => {
        setLoading(true);
        setTokens(null);
        setCreatedToken(null);
        fetchTokens();
    }, [fetchTokens]);

    if (!isAdminPermissions()) {
        return null;
    }

    const handleCreated = (token) => {
        setCreatedToken(token);
        fetchTokens();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {superAdmin && !organizationId && !createdToken && (
                <OrganizationSelect value={selectedOrganizationId} onChange={setSelectedOrganizationId} />
            )}
            {createdToken ? (
                <TokenCreatedPanel token={createdToken} onDismiss={() => setCreatedToken(null)} />
            ) : (
                <TokenCreateForm organizationId={selectedOrganizationId} onCreated={handleCreated} />
            )}
            <Divider sx={{ margin: '15px 0px 5px 0px' }} />
            <TokenList
                tokens={tokens}
                loading={loading}
                loadError={loadError}
                onRevoked={fetchTokens}
            />
            {onClose && !createdToken && (
                <Box sx={{ display: 'flex', justifyContent: 'center', margin: '10px 0px 0px 0px' }}>
                    <Button variant="text" onClick={onClose}>
                        {translate('ra.action.close')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};


export default ProvisioningTokens;
